import {
  BoundaryConfig,
  BoundaryConstraints,
  ContainerDimensions,
} from 'features/content-zoom/types';

export const defaultBoundaryConfig: BoundaryConfig = {
  maxDragDistance: 100,
  dragResistance: 0.6,
};

/**
 * Calculate boundary constraints for pan movement based on zoom scale and container/content dimensions
 */
export const calculateBoundaryConstraints = (
  scale: number,
  containerDimensions: ContainerDimensions
): BoundaryConstraints => {
  if (scale <= 1) {
    return {
      minX: -Infinity,
      maxX: Infinity,
      minY: -Infinity,
      maxY: Infinity,
    };
  }

  const scaledWidth = containerDimensions.width * scale;
  const scaledHeight = containerDimensions.height * scale;

  const maxScreenTranslationX = (scaledWidth - containerDimensions.width) / 2;
  const maxScreenTranslationY = (scaledHeight - containerDimensions.height) / 2;

  const maxPanX = maxScreenTranslationX / scale;
  const maxPanY = maxScreenTranslationY / scale;

  return {
    minX: -maxPanX,
    maxX: maxPanX,
    minY: -maxPanY,
    maxY: maxPanY,
  };
};

/**
 * Apply drag resistance when dragging outside bounds
 */
export const applyDragResistance = (
  offsetX: number,
  offsetY: number,
  boundaries: BoundaryConstraints,
  config: BoundaryConfig,
  scale: number = 1
): { offsetX: number; offsetY: number } => {
  const { maxDragDistance, dragResistance } = config;

  if (maxDragDistance === 0) {
    return { offsetX, offsetY };
  }

  const maxDragDistanceInOffsetCoords = maxDragDistance / scale;

  let constrainedX = offsetX;
  let constrainedY = offsetY;

  if (offsetX < boundaries.minX) {
    const distance = boundaries.minX - offsetX;
    const maxDistance = Math.min(distance, maxDragDistanceInOffsetCoords);
    const resistedDistance = maxDistance * dragResistance;
    constrainedX = boundaries.minX - resistedDistance;
  } else if (offsetX > boundaries.maxX) {
    const distance = offsetX - boundaries.maxX;
    const maxDistance = Math.min(distance, maxDragDistanceInOffsetCoords);
    const resistedDistance = maxDistance * dragResistance;
    constrainedX = boundaries.maxX + resistedDistance;
  }

  if (offsetY < boundaries.minY) {
    const distance = boundaries.minY - offsetY;
    const maxDistance = Math.min(distance, maxDragDistanceInOffsetCoords);
    const resistedDistance = maxDistance * dragResistance;
    constrainedY = boundaries.minY - resistedDistance;
  } else if (offsetY > boundaries.maxY) {
    const distance = offsetY - boundaries.maxY;
    const maxDistance = Math.min(distance, maxDragDistanceInOffsetCoords);
    const resistedDistance = maxDistance * dragResistance;
    constrainedY = boundaries.maxY + resistedDistance;
  }

  return { offsetX: constrainedX, offsetY: constrainedY };
};

/**
 * Constrain a position to stay within boundary limits
 */
export const constrainToBounds = (
  offsetX: number,
  offsetY: number,
  boundaries: BoundaryConstraints
): { offsetX: number; offsetY: number } => ({
  offsetX: Math.max(boundaries.minX, Math.min(boundaries.maxX, offsetX)),
  offsetY: Math.max(boundaries.minY, Math.min(boundaries.maxY, offsetY)),
});

/**
 * Check if a position is outside the boundary constraints
 */
export const isOutOfBounds = (
  offsetX: number,
  offsetY: number,
  boundaries: BoundaryConstraints
): boolean =>
  offsetX < boundaries.minX ||
  offsetX > boundaries.maxX ||
  offsetY < boundaries.minY ||
  offsetY > boundaries.maxY;
